/**
 * Admin › In Memoriam
 *
 * - Status snapshot for Wikipedia discovery + AI summaries + Phase 2 sources.
 * - "Discover now" button — kicks off a Wikipedia year crawl.
 * - Pending queue: approve / dismiss each auto-discovered entry.
 * - Bulk backfill AI summaries.
 */
import { useEffect, useState, useCallback } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Sparkles, CheckCircle2, Trash2, ExternalLink, Flower } from "lucide-react";
import AdminInMemoriamImports from "./AdminInMemoriamImports";

export default function AdminInMemoriam() {
  const [status, setStatus] = useState(null);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const currentYear = new Date().getUTCFullYear();
  const [year, setYear] = useState(currentYear);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        http.get("/admin/in-memoriam/status"),
        http.get("/admin/in-memoriam/pending"),
      ]);
      setStatus(s.data);
      setPending(Array.isArray(p.data) ? p.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const discover = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/admin/in-memoriam/discover", null, { params: { year } });
      toast.success(`Queued Wikipedia crawl for ${data.year}`);
      setTimeout(reload, 5000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Discovery failed");
    } finally {
      setBusy(false);
    }
  };

  const approve = async (pid) => {
    try {
      await http.post(`/admin/in-memoriam/${pid}/approve`);
      toast.success("Approved & AI summary queued");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    }
  };

  const dismiss = async (pid) => {
    if (!window.confirm("Dismiss this discovery? It will not appear again.")) return;
    try {
      await http.delete(`/admin/in-memoriam/${pid}`);
      toast.success("Dismissed");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dismiss failed");
    }
  };

  const backfillSummaries = async () => {
    try {
      await http.post("/admin/in-memoriam/summaries/backfill");
      toast.success("Bulk AI-summary backfill queued");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    }
  };

  const bulkApprove = async () => {
    const input = window.prompt("Auto-approve pending entries whose Wikipedia extract is at least how many characters?", "500");
    if (!input) return;
    const min = parseInt(input, 10);
    if (isNaN(min) || min < 100) return toast.error("Enter a number >= 100");
    try {
      const { data } = await http.post("/admin/in-memoriam/bulk-approve", null, { params: { min_extract_chars: min } });
      toast.success(`Approved ${data.approved} entries (extract >= ${data.threshold} chars)`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk approve failed");
    }
  };

  const bulkDismiss = async () => {
    const input = window.prompt("Dismiss pending entries whose Wikipedia extract is under how many characters? (Deletes rows.)", "150");
    if (!input) return;
    const max = parseInt(input, 10);
    if (isNaN(max) || max < 50 || max > 500) return toast.error("Enter a number between 50 and 500");
    if (!window.confirm(`Delete all pending entries with extract < ${max} chars? This cannot be undone.`)) return;
    try {
      const { data } = await http.post("/admin/in-memoriam/bulk-dismiss", null, { params: { max_extract_chars: max } });
      toast.success(`Dismissed ${data.dismissed} entries (extract < ${data.threshold} chars)`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk dismiss failed");
    }
  };

  const wiki = status?.wikipedia || {};
  const ai = status?.ai_summaries || {};
  const cwgc = status?.cwgc || {};
  const fs = status?.familysearch || {};

  return (
    <div data-testid="admin-in-memoriam-panel">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="overline mb-2 flex items-center gap-2">
            <Flower className="w-3.5 h-3.5" style={{ color: "var(--wofy-primary)" }} />
            IN MEMORIAM · MODERATION
          </div>
          <h2 className="font-display text-3xl">Wikipedia-sourced tribute directory.</h2>
          <p className="mt-2 max-w-2xl text-sm" style={{ color: "var(--wofy-text-soft)" }}>
            Auto-discovered deaths from Wikipedia's "Deaths in {currentYear}" category.
            Newly discovered notable people are queued for your review. People already in
            our World Changers database are auto-annotated as deceased.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="im-year-select"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="px-3 py-2 rounded-full border text-sm bg-transparent"
            style={{ borderColor: "var(--wofy-border)" }}
          >
            {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button
            data-testid="im-discover-btn"
            onClick={discover}
            disabled={busy}
            className="wofy-btn-primary"
          >
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Discover now
          </button>
        </div>
      </div>

      {/* Status cards */}
      <div className="mt-8 grid gap-4 md:grid-cols-4">
        <div className="wofy-card">
          <div className="overline mb-1">WIKIPEDIA</div>
          <div className="font-display text-2xl">{wiki.auto_discovered ?? 0}</div>
          <div className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>Discovered</div>
        </div>
        <div className="wofy-card">
          <div className="overline mb-1">PENDING</div>
          <div className="font-display text-2xl" data-testid="im-pending-count">{wiki.pending_review ?? 0}</div>
          <div className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>Awaiting approval</div>
        </div>
        <div className="wofy-card">
          <div className="overline mb-1">AI SUMMARIES</div>
          <div className="font-display text-2xl">{ai.with_summary ?? 0}</div>
          <div className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
            {ai.missing ?? 0} missing
          </div>
          <button
            onClick={backfillSummaries}
            data-testid="im-backfill-summaries-btn"
            className="mt-3 inline-flex items-center gap-1 text-xs underline"
          >
            <Sparkles className="w-3 h-3" /> Backfill missing
          </button>
        </div>
        <div className="wofy-card">
          <div className="overline mb-1">PHASE 2 SOURCES</div>
          <div className="mt-1 text-xs space-y-1" style={{ color: "var(--wofy-text-soft)" }}>
            <div className="flex justify-between">
              <span>CWGC</span>
              <span className={`font-mono ${cwgc.enabled ? "text-emerald-600" : "opacity-60"}`}>
                {cwgc.enabled ? `${cwgc.records ?? 0} records` : "scaffold"}
              </span>
            </div>
            <div className="flex justify-between">
              <span>FamilySearch</span>
              <span className={`font-mono ${fs.enabled ? "text-emerald-600" : "opacity-60"}`}>
                {fs.enabled ? `${fs.records ?? 0} records` : "no-creds"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk actions on pending queue */}
      <div
        className="mt-6 flex flex-wrap items-center gap-3 p-4 rounded-2xl border"
        style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
      >
        <div className="flex-1 min-w-[240px]">
          <div className="overline mb-1">BULK ACTIONS</div>
          <p className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
            Auto-approve notable entries (long Wikipedia extract) or prune low-signal ones. Everything remains reversible via the queue below.
          </p>
        </div>
        <button
          data-testid="im-bulk-approve-btn"
          onClick={bulkApprove}
          className="wofy-btn-primary text-sm"
        >
          <CheckCircle2 className="w-4 h-4" /> Auto-approve notable
        </button>
        <button
          data-testid="im-bulk-dismiss-btn"
          onClick={bulkDismiss}
          className="wofy-btn-ghost text-sm"
          style={{ color: "#B23A3A" }}
        >
          <Trash2 className="w-4 h-4" /> Dismiss low-signal
        </button>
      </div>

      {/* Pending queue */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-2xl">Pending approvals</h3>
          <button onClick={reload} className="wofy-btn-ghost text-xs">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
        {loading && <div className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>Loading…</div>}
        {!loading && pending.length === 0 && (
          <div className="wofy-card text-center py-12">
            <div className="font-display text-xl">Queue is clear.</div>
            <p className="mt-2 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
              Click <em>Discover now</em> to pull the latest deaths from Wikipedia.
            </p>
          </div>
        )}
        <div className="grid gap-3">
          {pending.map((p) => (
            <div
              key={p.id}
              data-testid={`im-pending-${p.slug || p.id}`}
              className="wofy-card flex flex-wrap items-center gap-5"
            >
              <div
                className="w-16 h-16 rounded-xl shrink-0 bg-cover bg-center"
                style={{
                  backgroundImage: `url(${p.photo_url || "/wofy-og-fallback.png"})`,
                  backgroundColor: "var(--wofy-surface)",
                }}
              />
              <div className="flex-1 min-w-[220px]">
                <div className="font-display text-lg">{p.name}</div>
                <div className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
                  {[p.born, p.died].filter(Boolean).join(" – ")}{p.occupation ? ` · ${p.occupation}` : ""}
                </div>
                {p.tagline && (
                  <p className="mt-1 text-sm line-clamp-2" style={{ color: "var(--wofy-text)" }}>
                    {p.tagline}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {p.wiki_url && (
                  <a
                    href={p.wiki_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`im-wiki-${p.slug || p.id}`}
                    className="wofy-btn-ghost text-xs"
                    aria-label="View Wikipedia source"
                  >
                    Source <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                <button
                  onClick={() => approve(p.id)}
                  data-testid={`im-approve-${p.slug || p.id}`}
                  className="wofy-btn-primary text-xs"
                >
                  <CheckCircle2 className="w-4 h-4" /> Approve
                </button>
                <button
                  onClick={() => dismiss(p.id)}
                  data-testid={`im-dismiss-${p.slug || p.id}`}
                  className="wofy-btn-ghost text-xs"
                  style={{ color: "#B23A3A" }}
                  aria-label="Dismiss"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CWGC + FamilySearch bulk import panels */}
      <AdminInMemoriamImports status={status} onReload={reload} />
    </div>
  );
}
